import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
// import { Carousel } from "react-responsive-carousel";
// import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Suspense, useEffect, useState } from "react";
import { hotlist } from "../constants/hotlist";
import { generateImageURL } from "../utils/convertCyrToLat";
import HeroCarousel from "../components/HeroCarousel";

const container = {
  hidden: { opacity: 1, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      delayChildren: 0,
    },
  },
};

const item = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
  },
};

// Специални ID-та на автомобилите
const specialIds = new Set([
  "VEH172205",
  "VEH172207",
  "VEH173195",
  "VEH173813",
  "VEH173816",
  "VEH173770",
  "VEH173771",
  "VEH173772",
  "VEH173773",
  "VEH172211",
]);

// Функция за обработка на грешки при зареждане на изображения
const onImageError = (e) => {
  e.target.src = "../images/default-car.jpg";
};

// Основен компонент
export default function CarListRedesign({ cars }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNoCarsMessage, setShowNoCarsMessage] = useState(false);

  // Получаване на параметри от URL
  const getParams = (param) =>
    searchParams.getAll(param).map((p) => p.toUpperCase()) || [];

  const modelsList = getParams("model");
  const fuelsList = getParams("fuel");
  const gearboxesList = getParams("gearbox");
  const vatsList = getParams("vat");

  // Филтриране на автомобили
  const filteredCars = cars.filter(
    (car) =>
      (modelsList.length === 0 ||
        modelsList.includes(car.model.toUpperCase())) &&
      (fuelsList.length === 0 || fuelsList.includes(car.fuel.toUpperCase())) &&
      (gearboxesList.length === 0 ||
        gearboxesList.includes(car.gearbox.toUpperCase())) &&
      (vatsList.length === 0 || vatsList.includes(car.vat.toUpperCase()))
  );

  //   const sortedCars = [...filteredCars].sort(
  //     (a, b) => a.new_price - b.new_price
  //   );

  // Сортиране по отстъпка
  const sortedCars = [...filteredCars].sort((a, b) => {
    const discountA = ((a.old_price - a.new_price) / a.old_price) * 100;
    const discountB = ((b.old_price - b.new_price) / b.old_price) * 100;

    // Ако и двата автомобила са в hotlist, сортирай по отстъпка
    if (hotlist.includes(a.id) && hotlist.includes(b.id)) {
      return discountB - discountA;
    }

    // Ако само единият е в hotlist, той трябва да е по-напред
    if (hotlist.includes(a.id)) return -1;
    if (hotlist.includes(b.id)) return 1;

    // Ако нито един не е в hotlist, сортирай по отстъпка
    return discountB - discountA;
  });

  // Генериране на налични модели
  // const availableModels = [...new Set(cars.map((car) => car.model))];

  // Изчистване на URL параметрите след 5 секунди, ако няма налични автомобили
  useEffect(() => {
    if (filteredCars.length === 0) {
      setShowNoCarsMessage(true);

      const timer = setTimeout(() => {
        setSearchParams({});
      }, 5000); // Изчистване след 5 секунди

      return () => clearTimeout(timer); // Почистете таймера при размонтиране
    } else {
      setShowNoCarsMessage(false);
    }
  }, [filteredCars.length, setSearchParams]);

  return (
    <div className="font-fordAntennaRegular">
      <HeroCarousel />
      <motion.div
        layout
        className="container grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4"
        variants={container}
        initial="hidden"
        animate="visible"
      >
        {sortedCars.length > 0 ? (
          sortedCars.map((car) => {
            const imageURL = specialIds.has(car.id)
              ? generateImageURL(car).replace(".jpeg", "-copy.jpeg")
              : generateImageURL(car);

            console.log("ID:", car.id, "→", imageURL);
            const discount =
              ((car.old_price - car.new_price) / car.old_price) * 100;

            return (
              <Link to={car.id} key={car.id}>
                <motion.div
                  layout
                  className="mt-4 overflow-hidden transition-all duration-200 border border-gray-200 rounded-2xl hover:border-gray-600"
                  variants={item}
                  id="the-list"
                >
                  {discount !== 0 && (
                    <div className="w-full px-4 py-2 text-center bg-green-200 rounded-t-2xl">
                      <p className="text-sm tracking-widest text-black uppercase">
                        {hotlist.includes(car.id)
                          ? "Ексклузивна оферта"
                          : "Специална оферта"}
                      </p>
                      <p className="text-sm tracking-widest text-black uppercase">
                        -
                        {new Intl.NumberFormat("bg", {
                          maximumFractionDigits: 0,
                        }).format(discount)}
                        %
                      </p>
                    </div>
                  )}
                  <Suspense fallback={<div>Зареждане...</div>}>
                    <img
                      src={imageURL}
                      alt={car.model}
                      onError={onImageError}
                    />
                  </Suspense>
                  <div className="mt-4 min-h-[92px] px-4">
                    <h3 className="text-2xl text-fordBlue font-fordBold">
                      {car.model}
                    </h3>
                    <p className="text-sm text-gray-500">Оферта {car.id}</p>
                  </div>
                  <div className="flex justify-between px-4 text-sm">
                    <p className="text-gray-500">Гориво</p>
                    <p className="text-fordBlue">{car.fuel}</p>
                  </div>
                  <div className="flex justify-between px-4 text-sm">
                    <p className="text-gray-500">Скоростна кутия</p>
                    <p className="text-fordBlue">{car.gearbox}</p>
                  </div>
                  <p className="p-4 mt-4 text-xl text-center bg-gray-50">
                    Цена:{" "}
                    {new Intl.NumberFormat("bg", {
                      minimumFractionDigits: 0,
                    }).format(car.new_price)}{" "}
                    лв. /{" "}
                    {new Intl.NumberFormat("bg", {
                      minimumFractionDigits: 0,
                    }).format(Math.round(car.new_price / 1.95583))}{" "}
                    € с ДДС
                  </p>
                </motion.div>
              </Link>
            );
          })
        ) : (
          <div className="py-8 mt-16 text-center border rounded-lg text-fordBlue col-span-full">
            <h2 className="text-2xl">
              В момента няма налични автомобили {modelsList}
            </h2>
          </div>
        )}
      </motion.div>
    </div>
  );
}
